<script lang="ts">
import {defineComponent} from 'vue'
import TicketLayouts from "@/components/Tickets/layouts/TicketLayouts.vue";
import {processObjectNullValues} from "@/common/processObjectNullValues";
import {formatIsoToCustom} from "@/common/formatToIso"
import api from "@/api/api"

export default defineComponent({
  name: "TicketStatuses",
  components: {TicketLayouts},
  data() {
    return {
      statusParams: {
        slug: "",
        name: "",
        enabled: true
      },
      ticketStatuses: [],
      isSidebarLoading: false,
      sidebarFields: [],
      isLoading: false,
      selectedStatus: {},
      fields: [
        {key: 'id', label: '№', sortable: true},
        {key: 'date', label: 'Дата', sortable: true},
        {key: 'name', label: 'Название'},
        {key: 'enabled', label: 'Включено',},
        {key: "actions", label: "Действия"}
      ]
    }
  },
  methods: {
    async getTicketStatuses() {
      this.isLoading = true;
      try {
        const res = await api.tickets.getListActionStatuses()
        this.ticketStatuses = res.data?.data?.map((status) => processObjectNullValues({
          id: status.id,
          slug: status.slug,
          name: status.name,
          enabled: status.enabled ? "Да" : "Нет",
          date: formatIsoToCustom(status.created_at),
          _original: status,
        }))
        this.isLoading = false;

      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || "Произошла ошибка", {
          title: "Ошибка сервера",
          variant: "danger",
        })
      } finally {
        this.isLoading = false;
      }
    },
    async createStatus() {
      if (this.statusParams.name === "") {
        this.$bvToast.toast('Введите название', {
          title: "Валидация",
          variant: "danger",
        })
        return false
      }
      if(this.statusParams.slug === "") {
        this.$bvToast.toast('Введите слоган', {
          title: "Валидация",
          variant: "danger",
        })
        return  false
      }
      try {
        const res = await api.tickets.createActionStatus(this.statusParams)
        const newStatus = res.data?.data
        const formattedStatus = processObjectNullValues({
          id: newStatus.id,
          name: newStatus.name,
          date: formatIsoToCustom(newStatus.created_at),
          username: newStatus.created_by?.username || newStatus.created_by?.email,
          enabled: newStatus.enabled ? 'Да' : 'Нет',
          _original: newStatus
        })
        this.ticketStatuses.push(formattedStatus)
        this.$bvModal.hide('create-status')
        this.statusParams.slug = ""
        this.statusParams.name = ""
      }catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async deleteStatus(status) {
      try {
        if (status && status.id) {
          await api.tickets.deleteActionStatus(status.id)
        }
        this.ticketStatuses = this.ticketStatuses.filter(item => item.id !== status.id)
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.message || e.response.data.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "danger",
        })
      }
    },
    async openEditSidebar(item) {
      this.sidebarFields = [
        {
          key: 'name',
          label: 'Название',
          type: 'text',
          placeholder: 'Введите название',
          required: true
        },
        {
          key: 'enabled',
          label: 'Включено',
          type: 'checkbox',
          default: true
        }
      ]
      this.selectedStatus = {
        id: item.id,
        name: item.name,
        enabled: item.enabled
      }
      this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
    },
    async handleSaveStatus(formData) {
      this.isSidebarLoading = true
      try {
        const res = await api.tickets.updateActionStatus(this.selectedStatus.id, {
          name: formData.name,
          enabled: formData.enabled === "Да"
        })
        const updatedStatus = res.data?.data || formData
        const index = this.ticketStatuses?.findIndex(type => type.id === formData.id)
        if (index !== -1) {
          const oldItem = this.ticketStatuses[index]
          this.$set(this.ticketStatuses, index, processObjectNullValues({
            id: updatedStatus.id,
            date: oldItem.date,
            name: updatedStatus.name,
            enabled: updatedStatus.enabled ? "Да" : "Нет",
            username: oldItem.username,
            slug: oldItem.slug,
            _original: updatedStatus
          }))
        }
        this.$bvToast.toast('Статус успешно обновлен', {
          title: "Успех",
          variant: "success",
        })
        this.$root.$emit('bv::toggle::collapse', 'sidebar-right')
        this.selectedStatus = null
      } catch (e) {
        console.log(e)
        this.$bvToast.toast(e.response.data.message ||e.response.message || 'Произошла ошибка', {
          title: "Ошибка сервера",
          variant: "success",
        })
      } finally {
        this.isSidebarLoading = false;
      }

    }
  },
  mounted() {
    this.getTicketStatuses()
  }
})
</script>

<template>
  <TicketLayouts
      @sidebar-save="handleSaveStatus"
      :sidebar-item="selectedStatus"
      :sidebar-fields="sidebarFields"
      :sidebar-loading="isSidebarLoading"
      title="Статусы Обращений"
  >
    <Breadcrumbs main="Tickets" title="Статусы Обращений"/>
    <div class="container-fluid">
      <div class="d-flex justify-content-end">
        <b-button v-b-modal.create-status variant="success">
          Создать
        </b-button>
      </div>
      <div>
        <b-table
            responsive
            show-empty
            stacked="md"
            :items="ticketStatuses"
            striped
            bordered
            :current-page=1
            :per-page=10
            :fields="fields"
        >
          <template #cell(actions)="{item}">
            <b-button
                class="mr-2"
                variant="primary"
                size="sm"
                @click="openEditSidebar(item)"
            >
              <i class="fas fa-edit"></i> Редактировать
            </b-button>
            <b-button
                variant="danger"
                size="sm"
                @click="deleteStatus(item)"
            >
              <i class="fas fa-trash"></i> Удалить
            </b-button>
          </template>
        </b-table>
      </div>
    </div>
    <b-modal
        id="create-status"
        title="Создать Статус"
        hide-footer
    >
      <b-form @submit.prevent="createStatus">
        <b-form-input v-model="statusParams.name" placeholder="Название"/>
        <b-form-input v-model="statusParams.slug" placeholder="Слоган" class="mt-3"/>
        <div class="mt-3 d-flex spacing gap-1">
          <b-form-checkbox v-model="statusParams.enabled" :value="statusParams.enabled"></b-form-checkbox>
          <p>Включено</p>
        </div>
        <b-button class="mt-3 w-100" type="submit">Создать</b-button>
      </b-form>

    </b-modal>
  </TicketLayouts>
</template>

<style scoped lang="scss">

</style>